import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from "@/components/ui/command";
import { Building2, MapPin, Hash, LayoutDashboard, Users, Store } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { de } from "date-fns/locale";

const STATUS_LABEL: Record<string, string> = {
  neu: "Neu",
  in_bearbeitung: "In Bearbeitung",
  offerten_eingegangen: "Offerten",
  abgeschlossen: "Abgeschlossen",
};

const STATUS_VARIANT: Record<string, "default" | "secondary" | "outline"> = {
  neu: "default",
  in_bearbeitung: "outline",
  offerten_eingegangen: "outline",
  abgeschlossen: "secondary",
};

interface Projekt {
  id: string;
  status: string | null;
  standort_ort: string;
  standort_plz: string;
  kanton: string;
  erstellt_am: string | null;
  kunde: { name: string } | null;
}

interface AdminCommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AdminCommandPalette({ open, onOpenChange }: AdminCommandPaletteProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [projekte, setProjekte] = useState<Projekt[]>([]);
  const [loading, setLoading] = useState(false);

  // Fetch projects whenever the palette opens
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    supabase
      .from("projekte")
      .select("id, status, standort_ort, standort_plz, kanton, erstellt_am, kunde:kunden!projekte_kunde_id_fkey(name)")
      .order("erstellt_am", { ascending: false })
      .limit(200)
      .then(({ data }) => {
        if (data) setProjekte(data as Projekt[]);
        setLoading(false);
      });
  }, [open]);

  const filtered = projekte.filter((p) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (
      p.kunde?.name?.toLowerCase().includes(q) ||
      p.standort_plz?.includes(q) ||
      p.standort_ort?.toLowerCase().includes(q) ||
      p.id.toLowerCase().includes(q)
    );
  });

  const handleSelect = useCallback(
    (projektId: string) => {
      onOpenChange(false);
      setQuery("");
      navigate(`/admin/projekt/${projektId}`);
    },
    [navigate, onOpenChange]
  );

  const handleNavSelect = useCallback(
    (url: string) => {
      onOpenChange(false);
      setQuery("");
      navigate(url);
    },
    [navigate, onOpenChange]
  );

  return (
    <CommandDialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setQuery(""); }}>
      <CommandInput
        placeholder="Projekt suchen: Name, PLZ, Ort oder ID..."
        value={query}
        onValueChange={setQuery}
      />
      <CommandList className="max-h-[420px]">
        <CommandEmpty>
          {loading ? "Lädt…" : "Keine Ergebnisse gefunden."}
        </CommandEmpty>

        {/* Quick navigation */}
        {!query && (
          <CommandGroup heading="Navigation">
            <CommandItem value="nav-dashboard" onSelect={() => handleNavSelect("/admin")}>
              <LayoutDashboard className="mr-2 h-4 w-4 text-muted-foreground" />
              Dashboard
            </CommandItem>
            <CommandItem value="nav-projekte" onSelect={() => handleNavSelect("/admin/projekte")}>
              <Building2 className="mr-2 h-4 w-4 text-muted-foreground" />
              Projekte
            </CommandItem>
            <CommandItem value="nav-kunden" onSelect={() => handleNavSelect("/admin/kunden")}>
              <Users className="mr-2 h-4 w-4 text-muted-foreground" />
              Kunden
            </CommandItem>
            <CommandItem value="nav-anbieter" onSelect={() => handleNavSelect("/admin/anbieter")}>
              <Store className="mr-2 h-4 w-4 text-muted-foreground" />
              Anbieter
            </CommandItem>
          </CommandGroup>
        )}

        {!query && <CommandSeparator />}

        {filtered.length > 0 && (
          <CommandGroup heading={query ? `Projekte (${filtered.length})` : "Neueste Projekte"}>
            {(query ? filtered : filtered.slice(0, 8)).map((p) => (
              <CommandItem
                key={p.id}
                value={`proj-${p.id} ${p.kunde?.name ?? ""} ${p.standort_plz} ${p.standort_ort}`}
                onSelect={() => handleSelect(p.id)}
                className="flex items-center gap-2 py-2"
              >
                <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="flex flex-col flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium truncate">{p.kunde?.name ?? "–"}</span>
                    <Badge variant={STATUS_VARIANT[p.status ?? ""] ?? "outline"} className="text-[10px] px-1.5 py-0 shrink-0">
                      {STATUS_LABEL[p.status ?? ""] ?? p.status}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                    <span className="flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      {p.standort_plz} {p.standort_ort}, {p.kanton}
                    </span>
                    {p.erstellt_am && (
                      <span>{format(new Date(p.erstellt_am), "dd.MM.yyyy", { locale: de })}</span>
                    )}
                    <span className="flex items-center gap-1 font-mono opacity-60">
                      <Hash className="h-3 w-3" />
                      {p.id.slice(0, 8)}
                    </span>
                  </div>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>

      {/* Footer hint */}
      <div className="flex items-center justify-between px-3 py-2 border-t border-border bg-muted/30 text-xs text-muted-foreground">
        <span>↑↓ navigieren</span>
        <span>↵ öffnen</span>
        <span>esc schliessen</span>
      </div>
    </CommandDialog>
  );
}
